import React, {useMemo, useState} from 'react';
import {Tag} from "antd";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, getFirestore, limit, orderBy, query} from "firebase/firestore";
import moment from 'moment';
import {Link} from "react-router-dom";
import Firebase from "../../Firebase/Firebase";

const db = getFirestore(Firebase);

const STATUS_COLOR: Record<string, string> = {successful: 'green', pending: 'orange', failed: 'red'};
const mwk = (n: number) => `MWK ${Math.round(n).toLocaleString('en-US')}`;
const toDate = (v: any) => (v && typeof v.toDate === 'function' ? v.toDate() : null);

/** "Support Shopped" payments (supportPayments) — read-only; written by Cloud Functions. */
export default function SupportPayments() {
    const [snap, loading, error] = useCollection(query(collection(db, 'supportPayments'), orderBy('createdAt', 'desc'), limit(500)));
    const [show, setShow] = useState<'successful' | 'pending' | 'failed' | 'all'>('successful');

    const docs = useMemo(() => snap?.docs.map((d) => ({id: d.id, ...(d.data() as any)})) ?? [], [snap]);
    const totals = useMemo(() => {
        const ok = docs.filter((p) => p.status === 'successful');
        const monthStart = moment().startOf('month').toDate();
        const month = ok.filter((p) => (toDate(p.createdAt) ?? new Date(0)) >= monthStart);
        const sum = (a: any[]) => a.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        return {all: sum(ok), allCount: ok.length, month: sum(month), monthCount: month.length, supporters: new Set(ok.map((p) => p.userId)).size};
    }, [docs]);

    const rows = docs.filter((p) => show === 'all' || p.status === show);

    const card = (label: string, value: string, sub: string) => (
        <div style={{flex: '1 1 180px', background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14}}>
            <div style={{fontSize: 12, color: '#888'}}>{label}</div>
            <div style={{fontSize: 20, fontWeight: 800, color: '#29327b'}}>{value}</div>
            <div style={{fontSize: 12, color: '#999'}}>{sub}</div>
        </div>
    );

    return (
        <div>
            <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14}}>
                {card('This month', mwk(totals.month), `${totals.monthCount} payments`)}
                {card('All time', mwk(totals.all), `${totals.allCount} payments`)}
                {card('Supporters', String(totals.supporters), 'people')}
            </div>
            <div style={{marginBottom: 10}}>
                <select value={show} onChange={(e) => setShow(e.target.value as any)}
                        style={{fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd'}}>
                    <option value="successful">Received</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="all">All</option>
                </select>
            </div>
            {loading && <div style={{padding: 20, color: '#999'}}>Loading…</div>}
            {error && <div style={{padding: 20, color: '#b91c1c'}}>{error.message}</div>}
            {!loading && rows.length === 0 && <div style={{padding: 20, color: '#999'}}>Nothing here.</div>}
            {rows.map((p) => (
                <div key={p.id} style={{background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 12, marginBottom: 6}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'}}>
                        <b>{mwk(Number(p.amount) || 0)}</b>
                        <span style={{fontSize: 12, color: '#999'}}>{toDate(p.createdAt) ? moment(toDate(p.createdAt)).format('DD MMM YYYY, HH:mm') : ''}</span>
                    </div>
                    <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                        <Link to={`/buyers/${p.userId}`}>{p.userName || p.userId}</Link>
                        {' · '}{p.method === 'bank_transfer' ? 'Bank transfer' : `${p.correspondent === 'TNM_MWI' ? 'TNM Mpamba' : 'Airtel Money'} ••${p.phoneLast4 ?? ''}`}
                        {' · '}<Tag color={STATUS_COLOR[p.status] ?? 'default'}>{p.status === 'successful' ? 'Received' : p.status}</Tag>
                    </div>
                    {p.message && <div style={{fontSize: 13, marginTop: 6, fontStyle: 'italic'}}>“{p.message}”</div>}
                    <div style={{fontSize: 11, color: '#bbb', marginTop: 4}}>Ref {p.id}</div>
                </div>
            ))}
        </div>
    );
}
